// Modified Home.jsx with root autocomplete support, keyboard navigation, and sarf-based root suggestions
import { useState, useEffect, useLayoutEffect, useRef } from "react";
import {
  Box,
  Typography,
  FilledInput,
  IconButton,
  useTheme,
  Paper,
  Tooltip,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useHistory, Link } from "react-router-dom";
import { consumeTour } from "../../utils/walkthroughTrigger";
import Swal from "sweetalert2";
import {
  toastError,
  toastTip,
  addToastSwipeToDismiss,
  toSearchKey,
  searchPath,
  transliterationHint,
} from "../../utils/utils";
import { KEYBOARD_OPEN_CLASS, closeSoftKeyboard, onSoftKeyboardHidden } from "../../utils/softKeyboard";
import {
  getMatchingRoots,
  getRandomEntry,
  initDictionaryDB,
  retrieveAllWordsWithRoot,
} from "../../utils/dictionary-db";
import { spellRootKeys } from "../../utils/rootDisplay";
import TypingAnimation, { PREFIX as PLACEHOLDER_PREFIX } from "./typingAnimation";
import StyleIcon from "@mui/icons-material/Style";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardIcon from "@mui/icons-material/Keyboard";

import { Dialog, DialogContent, Fade } from "@mui/material";
import FeedbackDialog from "../FeedbackDialog";
import PageHeader from "../PageHeader";
import HomeWalkthrough from "./HomeWalkthrough";
import useSearchHotkeys from "../../utils/useSearchHotkeys";
import VersionBadge from "../VersionBadge";

const ButtonsBox = () => {
  return (
    <Box sx={{ display: { xs: "none", sm: "block" } }}>
      <Tooltip title="Bookmarks">
        <IconButton
          to="/bookmarks"
          component={Link}
          sx={{
            borderRadius: 2,
            p: 2,
            marginRight: 2,
            color: "#fff",
            background: (theme) => theme.palette.blue,
            boxShadow: "0px 10px 10px rgba(221, 114, 133, 0.2)",
            marginBottom: 20,
            transition: "transform 0.3s ease-in-out",
            ":hover": {
              transform: "scale(1.1)",
            },
          }}
        >
          <BookmarkIcon />
        </IconButton>
      </Tooltip>

      <Tooltip title="Flashcards">
        <IconButton
          to="/flashcards"
          component={Link}
          sx={{
            borderRadius: 2,
            p: 2,
            color: "#fff",
            background: (theme) => theme.palette.blueAlternative,
            boxShadow: "0px 10px 10px rgba(221, 114, 133, 0.2)",
            marginBottom: 20,
            transition: "transform 0.3s ease-in-out",
            ":hover": {
              transform: "scale(1.1)",
            },
          }}
        >
          <StyleIcon />
        </IconButton>
      </Tooltip>
    </Box>
  );
};


const Toast = Swal.mixin({
  toast: true,
  position: "bottom-end",
  showConfirmButton: false,
  showCloseButton: true,
  timer: 10000,
  timerProgressBar: true,
  didOpen: (toast) => {
    toast.addEventListener("mouseenter", Swal.stopTimer);
    toast.addEventListener("mouseleave", Swal.resumeTimer);
    addToastSwipeToDismiss();
  },
});

const DROPDOWN_MAX_PX = 240;
const DROPDOWN_MIN_PX = 44; // about one suggestion row
// On phones, a focused search on a viewport shorter than this (soft keyboard open, or
// landscape) hides the logo, title and footer so the suggestions get the space above
// the keyboard. Below TINY (landscape with the keyboard open) the header goes too.
const COMPACT_BELOW_PX = 600;
const TINY_BELOW_PX = 300;

const visibleHeight = () => window.visualViewport?.height ?? window.innerHeight;
const isDesktopPointer = () =>
  typeof window.matchMedia === "function" &&
  window.matchMedia("(hover: hover) and (pointer: fine)").matches;

const Home = () => {
  const [word, setWord] = useState("");
  // Index keys, which are what gets searched, and how the dictionary spells each.
  const [suggestions, setSuggestions] = useState([]);
  const [spellings, setSpellings] = useState(() => new Map());
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [sarfSuggestions, setSarfSuggestions] = useState([]);
  const theme = useTheme();
  const history = useHistory();
  const inputRef = useRef(null);
  const [isClosing, setIsClosing] = useState(false);
  const paperRef = useRef(null);
  const closeTimerRef = useRef(null);
  const lastSuggestionsRef = useRef([]);
  if (suggestions.length > 0) lastSuggestionsRef.current = suggestions;
  // Captured during render (before DOM commit) so useLayoutEffect has the old height
  const snapshotHeightRef = useRef(0);
  snapshotHeightRef.current = paperRef.current?.offsetHeight ?? snapshotHeightRef.current;
  const searchDebounceRef = useRef(null);
  // Counts lookups, so that one finishing after a newer one started, or after the list
  // was closed, is dropped.
  const lookupRef = useRef(0);
  const typedExample = TypingAnimation();


  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [randomEntry, setRandomEntry] = useState(null);
  const [randomLoading, setRandomLoading] = useState(false);
  const [showHomeWalkthrough, setShowHomeWalkthrough] = useState(false);
  const walkthroughStarted = useRef(false);
  const tourPendingRef = useRef(false);
  const tourTimerRef = useRef(null);
  const blurTimerRef = useRef(null);
  const contentRef = useRef(null);
  const searchBoxRef = useRef(null);
  const listRef = useRef(null);
  const [searchFocused, setSearchFocused] = useState(false);
  const [viewportHeight, setViewportHeight] = useState(visibleHeight);
  const [desktopPointer] = useState(isDesktopPointer);
  const compact =
    searchFocused && !desktopPointer && !showHomeWalkthrough && viewportHeight < COMPACT_BELOW_PX;
  const tiny = compact && viewportHeight < TINY_BELOW_PX;

  const closeSuggestions = () => {
    clearTimeout(searchDebounceRef.current);
    lookupRef.current += 1;
    setSuggestions([]);
    setHighlightedIndex(-1);
  };

  // "/" focuses the search bar; "Escape" clears it.
  useSearchHotkeys(inputRef, () => {
    setWord("");
    closeSuggestions();
  });

  // Android's Back puts the keyboard away but leaves the field focused, and the list would
  // stay over the page. It closes with the keyboard (the text stays); a tap on the field or
  // more typing opens it again.
  useEffect(() => onSoftKeyboardHidden((field) => {
    if (field === inputRef.current) closeSuggestions();
  }), []); // eslint-disable-line react-hooks/exhaustive-deps

  // Cap the dropdown to the space actually visible below it: above the soft keyboard
  // (visualViewport) and inside the scrolling content box, which clips it.
  const fitDropdown = () => {
    const list = listRef.current;
    const paper = paperRef.current;
    const box = contentRef.current;
    const anchor = searchBoxRef.current;
    if (!list || !paper || !box || !anchor) return;
    const vv = window.visualViewport;
    const visibleBottom = Math.min(
      vv ? vv.offsetTop + vv.height : window.innerHeight,
      box.getBoundingClientRect().bottom
    );
    const room = Math.floor(visibleBottom - (anchor.getBoundingClientRect().top + paper.offsetTop) - 8);
    list.style.maxHeight = `${Math.min(DROPDOWN_MAX_PX, Math.max(DROPDOWN_MIN_PX, room))}px`;
  };

  useEffect(() => {
    const vv = window.visualViewport;
    const onViewportChange = () => {
      setViewportHeight(visibleHeight());
      fitDropdown();
    };
    window.addEventListener("resize", onViewportChange);
    vv?.addEventListener("resize", onViewportChange);
    vv?.addEventListener("scroll", onViewportChange);
    return () => {
      window.removeEventListener("resize", onViewportChange);
      vv?.removeEventListener("resize", onViewportChange);
      vv?.removeEventListener("scroll", onViewportChange);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => {
    clearTimeout(tourTimerRef.current);
    clearTimeout(blurTimerRef.current);
    clearTimeout(searchDebounceRef.current);
  }, []);

  // Compact mode leaves the search box first in the content; show it from the top.
  useLayoutEffect(() => {
    if (compact && contentRef.current) contentRef.current.scrollTop = 0;
  }, [compact]);

  // Before the height animation below, so it measures the capped height.
  useLayoutEffect(() => {
    if (suggestions.length > 0) fitDropdown();
  }, [suggestions, compact, viewportHeight]); // eslint-disable-line react-hooks/exhaustive-deps

  useLayoutEffect(() => {
    clearTimeout(closeTimerRef.current);

    if (suggestions.length === 0) {
      if (snapshotHeightRef.current > 0) {
        // Trigger close animation; keep Paper alive via isClosing
        setIsClosing(true);
        closeTimerRef.current = setTimeout(() => {
          setIsClosing(false);
          snapshotHeightRef.current = 0;
        }, 160);
      }
      return;
    }

    // Cancel any in-progress close
    setIsClosing(false);

    // Shrink animation
    const paper = paperRef.current;
    if (!paper) return;
    const oldH = snapshotHeightRef.current;
    const newH = paper.offsetHeight;
    if (newH >= oldH || oldH === 0) return;
    paper.style.height = `${oldH}px`;
    paper.style.overflow = "hidden";
    paper.style.transition = "none";
    void paper.offsetHeight;
    paper.style.transition = "height 0.22s cubic-bezier(0.4,0,0.2,1)";
    paper.style.height = `${newH}px`;
    const t = setTimeout(() => {
      paper.style.height = "";
      paper.style.overflow = "";
      paper.style.transition = "";
    }, 240);
    return () => {
      clearTimeout(t);
      paper.style.height = "";
      paper.style.overflow = "";
      paper.style.transition = "";
    };
  }, [suggestions]); // eslint-disable-line react-hooks/exhaustive-deps

  // The first-run tour takes focus (closing the soft keyboard), so it never starts while the
  // search field is focused or holds text; the next blur tries again. Nor over a dialog,
  // sheet or menu (the Typing Guide, the header menu): its overlay would cover it, and the
  // modal's focus trap would leave neither usable. Then the next blur or Home visit retries.
  const startTourWhenIdle = (delay) => {
    clearTimeout(tourTimerRef.current);
    tourTimerRef.current = setTimeout(() => {
      const input = inputRef.current;
      if (!tourPendingRef.current || !input) return;
      if (document.activeElement === input || input.value.trim()) return;
      // A closed keepMounted modal stays in the DOM as MuiModal-hidden.
      if (document.querySelector('.MuiModal-root:not(.MuiModal-hidden), [role="dialog"]')) return;
      tourPendingRef.current = false;
      setShowHomeWalkthrough(true);
    }, delay);
  };

  // Auto-show on first launch; also consume a pending tour scheduled by the sidebar
  useEffect(() => {
    if (consumeTour()) {
      setShowHomeWalkthrough(true);
      return;
    }
    if (!walkthroughStarted.current && !localStorage.getItem("hasSeenHomeWalkthrough")) {
      walkthroughStarted.current = true;
      tourPendingRef.current = true;
      startTourWhenIdle(1000);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // However the tour was opened, the pending first-run one is no longer needed.
  useEffect(() => {
    if (showHomeWalkthrough) tourPendingRef.current = false;
  }, [showHomeWalkthrough]);

  // Sidebar fires this event when user is already on the home page
  useEffect(() => {
    const handler = () => setShowHomeWalkthrough(true);
    window.addEventListener("hw-start-tour", handler);
    return () => window.removeEventListener("hw-start-tour", handler);
  }, []);

  const startWalkthrough = () => {
    localStorage.removeItem("hasSeenDefinitionWalkthrough");
    setShowHomeWalkthrough(true);
  };

  const showTourHint = () => {
    // On desktop use the sidebar button; on mobile fall back to the hamburger
    let anchor = document.getElementById("wt-sidebar-tour");
    let below = false;
    if (!anchor || anchor.getBoundingClientRect().width === 0) {
      anchor = document.getElementById("wt-menu-btn");
      below = true;
    }
    if (!anchor) return;

    anchor.classList.add("hw-pulse");
    if (below) anchor.classList.add("hw-pulse-round");
    const rect = anchor.getBoundingClientRect();
    const tip = document.createElement("div");
    tip.className = "hw-sidebar-hint" + (below ? " hw-hint-below" : "");
    if (below) {
      tip.style.top = `${rect.bottom + 10}px`;
      // Its arrow (20px in from its left edge) under the button's centre.
      tip.style.left = `${rect.left + rect.width / 2 - 20}px`;
    } else {
      tip.style.top = `${rect.top + rect.height / 2}px`;
      tip.style.left = `${rect.right + 10}px`;
    }
    tip.textContent = "The guided tour is always available here.";
    document.body.appendChild(tip);
    const dismiss = () => {
      tip.classList.add("hw-hint-fadeout");
      setTimeout(() => {
        tip.remove();
        anchor.classList.remove("hw-pulse", "hw-pulse-round");
      }, 350);
    };
    setTimeout(dismiss, 4000);
  };

  const handleHomeWalkthroughDone = () => {
    localStorage.setItem("hasSeenHomeWalkthrough", "1");
    localStorage.removeItem("hasSeenDefinitionWalkthrough");
    setShowHomeWalkthrough(false);
    history.push("/search/كتب");
  };

  const handleHomeWalkthroughSkip = () => {
    localStorage.setItem("hasSeenHomeWalkthrough", "1");
    setShowHomeWalkthrough(false);
    showTourHint();
  };

  const loadRandomEntry = async () => {
    setRandomLoading(true);
    const entry = await getRandomEntry();
    setRandomEntry(entry);
    setRandomLoading(false);
  };

  useEffect(() => {
    initDictionaryDB().then(loadRandomEntry);
  }, []);



  const scheduleSuggestions = (input, delay) => {
    clearTimeout(searchDebounceRef.current);
    const lookup = (lookupRef.current += 1);
    // Harakat-only input has no key; looking up "" would list the whole index.
    const key = toSearchKey(input);
    if (!key) {
      setSuggestions([]);
      return;
    }

    searchDebounceRef.current = setTimeout(async () => {
      const matches = (await getMatchingRoots(key)).slice(0, 10);
      // Keys drop hamza seats (قرا for قرأ): list each root as the dictionary spells it.
      const spelled = await spellRootKeys(matches).catch(() => new Map());
      // Drop a stale lookup, and stay closed if the field lost focus while it ran.
      if (lookup !== lookupRef.current || document.activeElement !== inputRef.current) return;
      setSpellings(spelled);
      setSuggestions(matches);
    }, delay);
  };

  const handleChange = (e) => {
    const input = e.target.value;
    setWord(input);
    setHighlightedIndex(-1);
    scheduleSuggestions(input, 150);
  };

  const handleFocus = () => {
    clearTimeout(blurTimerRef.current);
    setSearchFocused(true);
    if (word.trim()) scheduleSuggestions(word, 0);
  };

  // A tap on the field that kept focus when Back put the keyboard away brings no focus event.
  const handleClick = () => {
    if (suggestions.length === 0 && word.trim()) scheduleSuggestions(word, 0);
  };

  const handleBlur = () => {
    closeSuggestions();
    // Leave compact mode only after the tap that blurred the field has been delivered:
    // bringing the logo back shifts the layout, and the tap would land on the wrong element.
    blurTimerRef.current = setTimeout(() => setSearchFocused(false), 200);
    if (tourPendingRef.current) startTourWhenIdle(800);
  };

  const handleSelectSuggestion = (selected) => {
    setSarfSuggestions([]);
    setSuggestions([]);

    history.push({ pathname: searchPath(selected), state: { rawQuery: word } });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const trimmedWord = word.trim();
    // Input that is only harakat, tatweel or punctuation leaves nothing to search for.
    const key = toSearchKey(trimmedWord);
    // Either toast closes the soft keyboard first, or on iOS it would be drawn under it.
    if (!key) {
      closeSoftKeyboard();
      toastError("Root to search cannot be empty.");
      return;
    }
    // Letters the typing guide has no Arabic for ("book"): say so, rather than open a
    // "Root not found" page for a key that can't match.
    const hint = transliterationHint(key);
    if (hint) {
      closeSoftKeyboard();
      toastTip(hint);
      return;
    }

    setSarfSuggestions([]);
    setSuggestions([]);
    // Always navigate to definition page, let it handle the fallback
    history.push({ pathname: searchPath(key), state: { rawQuery: trimmedWord } });
  };

  const handleKeyDown = (e) => {
    if (suggestions.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) => (prev + 1) % suggestions.length);
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex(
          (prev) => (prev - 1 + suggestions.length) % suggestions.length
        );
        break;
      case "Enter":
        if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
          handleSelectSuggestion(suggestions[highlightedIndex]);
        } else {
          handleSubmit(e);
        }
        break;
      default:
        break;
    }
  };

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: { xs: 0, sm: "var(--hw-sidebar-w)" },
        right: 0,
        transition: "left 0.25s cubic-bezier(0.4,0,0.2,1)",
        // Clear of the BottomNav, except while the soft keyboard hides it: then the
        // suggestions get the space down to the keyboard.
        bottom: { xs: "calc(64px + env(safe-area-inset-bottom, 0px))", sm: 0 },
        [`body.${KEYBOARD_OPEN_CLASS} &`]: { bottom: 0 },
        px: 2,
        // PageHeader clears the status bar; when tiny mode hides it, this does.
        pt: tiny ? "env(safe-area-inset-top, 0px)" : 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        animation: "pageIn 0.22s ease-out both",
      }}
    >
      <Box sx={{ display: tiny ? "none" : "contents" }}>
        <PageHeader title="Hans Wehr" isHome onWalkthroughClick={startWalkthrough} />
      </Box>

      {/* Centred by the first and last children's auto margins, not justify-content: center,
          which pushes overflow above the scroll origin where it can't be scrolled to. */}
      <Box ref={contentRef} onScroll={fitDropdown} sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", overflowY: "auto", minHeight: 0, scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" } }}>

      <Box
        sx={{
          display: compact ? "none" : "flex",
          justifyContent: "center",
          alignItems: "center",
          width: { xs: "min(85vw, 360px)", sm: "360px" },
          height: { xs: 150, sm: 180 },
          // Short screens (laptops, phone landscape) shrink the logo along with the card
          minHeight: 96,
          mt: "auto",
          animation: "logoIn 0.4s ease-out both",
        }}
      >
        <img
          src="/assets/book.png"
          alt="Book"
          width="684"
          height="473"
          style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
        />
      </Box>

      {/* App chrome, like the header: a long press shouldn't select it. */}
      <Box sx={{ display: compact ? "none" : "block", flexShrink: 0, animation: "pageIn 0.22s ease-out both", animationDelay: "80ms", textAlign: "center", userSelect: "none" }}>
        <Typography color="primary" sx={{ mt: 1.5, mb: 0.5 }} variant="h4">
          Hans Wehr
        </Typography>
        <Typography color="GrayText">Find meanings of arabic roots</Typography>
      </Box>

      <Box id="wt-home-search" ref={searchBoxRef} sx={{ width: { xs: "min(100%, 360px)", sm: "360px" }, position: "relative", display: "flow-root", flexShrink: 0, animation: "pageIn 0.22s ease-out backwards", animationDelay: "140ms" }}>
        <form onSubmit={handleSubmit} spacing={0} autoComplete="off">
          <Box sx={{ position: "relative", my: compact ? 1 : 2 }}>
          <FilledInput
            inputRef={inputRef}
            value={word}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            onBlur={handleBlur}
            disableUnderline
            inputProps={{ autoCapitalize: "none", autoCorrect: "off", spellCheck: false, "aria-label": "Search for a root", onClick: handleClick }}
            sx={{
              backgroundColor: "white",
              borderRadius: 2,
              boxShadow: "0px 10px 25px rgba(0, 0, 0, 0.05)",
              transition: "transform 0.2s",
              "&:focus-within": { transform: "scale(1.02)" },
              height: "48px",
              display: "flex",
              alignItems: "center",
              fontSize: "16px",
              "& .MuiFilledInput-input": {
                padding: "0 14px",
                height: "100%",
                lineHeight: 1,
                fontFamily: "'Noto Naskh Arabic', 'Scheherazade', Mulish, sans-serif",
                "&::placeholder": { color: "transparent" },
              },
            }}
            startAdornment={<SearchIcon color="disabled" />}
            endAdornment={
              // Buttons give the icons names and focus for TalkBack and keyboards. Padding
              // with an equal negative margin makes each a 48px touch target spanning the
              // input's height without moving its icon: the gap is split between the two,
              // and ? also reaches over the input's end padding.
              <InputAdornment position="end" sx={{ display: "flex", gap: "27px" }}>
                <Tooltip title="Typing guide">
                  <IconButton
                    id="wt-home-guide"
                    aria-label="Typing guide"
                    // During the tour the dialog would open beneath its overlay
                    onClick={() => !showHomeWalkthrough && setGuideOpen(true)}
                    sx={{
                      p: "15px",
                      m: "-15px",
                      color: "#aaa",
                      "&:hover": { color: "#555", bgcolor: "transparent" },
                    }}
                  >
                    <HelpOutlineIcon sx={{ fontSize: "1.125rem" }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Search">
                  <IconButton
                    aria-label="Search"
                    onClick={handleSubmit}
                    sx={{
                      p: "12px",
                      m: "-12px",
                      "&:hover": { bgcolor: "transparent" },
                      "&:hover .MuiSvgIcon-root": {
                        color: "black",
                        backgroundColor: "#BABABA",
                        borderRadius: "50%",
                      },
                    }}
                  >
                    <ArrowForwardIcon />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            }
            fullWidth
          />
          {!word && (
            <Box
              aria-hidden
              sx={{
                position: "absolute",
                top: 0,
                bottom: 0,
                // Paddings in px plus the icons in rem, so this still matches the input's
                // text area when the system font size scales the icons.
                left: `calc(26px + ${theme.typography.pxToRem(24)})`,
                right: `calc(61px + ${theme.typography.pxToRem(24)} + 1.125rem)`,
                display: "flex",
                alignItems: "center",
                pointerEvents: "none",
                color: "rgba(0,0,0,0.38)",
                fontFamily: "'Noto Naskh Arabic','Scheherazade',Mulish,sans-serif",
                fontSize: "1rem",
                lineHeight: 1,
                overflow: "hidden",
                whiteSpace: "nowrap",
              }}
            >
              {/* At large text sizes the prefix ellipsizes so the example stays readable */}
              <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "pre" }}>
                {PLACEHOLDER_PREFIX}
              </Box>
              <Box component="span" sx={{ flexShrink: 0 }}>{typedExample}</Box>
            </Box>
          )}
          </Box>
        </form>

        {(suggestions.length > 0 || isClosing) && (
          <Paper
            ref={paperRef}
            elevation={0}
            // Keep focus in the field while a suggestion is pressed, so the blur that
            // closes the dropdown doesn't remove it before the click selects.
            onMouseDown={(e) => e.preventDefault()}
            sx={{
              position: "absolute",
              zIndex: 10,
              width: "100%",
              mt: 0.75,
              borderRadius: "14px",
              border: "1px solid rgba(21,53,189,0.1)",
              boxShadow: "0px 8px 32px rgba(21,53,189,0.14)",
              overflow: "hidden",
              animation: isClosing
                ? "suggestionDropdownOut 0.15s ease-in forwards"
                : "suggestionDropdown 0.18s ease-out both",
            }}
          >
            <Box ref={listRef} sx={{ maxHeight: DROPDOWN_MAX_PX, overflowY: "auto", overscrollBehavior: "contain", scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" } }}>
              {(isClosing ? lastSuggestionsRef.current : suggestions).map((s, i) => {
                const label = spellings.get(s) || s;
                const isArabic = /[\u0600-\u06FF]/.test(label);
                const isSelected = i === highlightedIndex;
                return (
                  <Box
                    key={s}
                    onClick={() => handleSelectSuggestion(s)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      px: 2,
                      py: 1,
                      cursor: "pointer",
                      borderLeft: isSelected ? "3px solid #1535BD" : "3px solid transparent",
                      bgcolor: isSelected ? "rgba(21,53,189,0.06)" : "transparent",
                      transition: "background-color 0.12s, border-left 0.12s",
                      animation: "suggestionItem 0.15s ease-out both",
                      animationDelay: `${Math.min(i, 7) * 28}ms`,
                      "&:hover": { bgcolor: "rgba(21,53,189,0.06)", borderLeft: "3px solid #1535BD" },
                    }}
                  >
                    <Typography sx={{
                      flex: 1,
                      fontSize: isArabic ? "1.1rem" : "0.95rem",
                      fontFamily: isArabic ? "'Noto Naskh Arabic', 'Scheherazade', serif" : "Mulish, sans-serif",
                      fontWeight: isArabic ? 500 : 600,
                      color: isSelected ? "#1535BD" : "#222",
                      lineHeight: 1.5,
                    }}>
                      {label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Paper>
        )}

        {sarfSuggestions.length > 0 && (
          <Box sx={{ mt: 1, mb: 1.5, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, animation: "suggestionDropdown 0.18s ease-out both" }}>
            <Typography sx={{ fontSize: "0.72rem", color: "text.secondary", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase" }}>
              Did you mean:
            </Typography>
            {sarfSuggestions.map((root, idx) => (
              <Box
                key={idx}
                onClick={() => handleSelectSuggestion(root)}
                sx={{
                  px: 1.5, py: 0.5,
                  borderRadius: "8px",
                  bgcolor: "rgba(21,53,189,0.07)",
                  border: "1px solid rgba(21,53,189,0.15)",
                  cursor: "pointer",
                  transition: "background-color 0.15s, transform 0.1s",
                  "&:hover": { bgcolor: "rgba(21,53,189,0.14)", transform: "scale(1.04)" },
                  animation: "suggestionItem 0.15s ease-out both",
                  animationDelay: `${idx * 40}ms`,
                }}
              >
                <Typography sx={{ fontFamily: "'Noto Naskh Arabic', 'Scheherazade', serif", fontSize: "1rem", color: "#1535BD", fontWeight: 600, lineHeight: 1.4 }}>
                  {root}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* Learn a new root card */}
      <Box
        id="wt-home-card"
        onClick={() => randomEntry && history.push(searchPath(randomEntry.searchableRoots?.[0] || randomEntry.root))}
        sx={{
          display: "flex",
          flexDirection: "column",
          width: { xs: "min(100%, 360px)", sm: "360px" },
          // On short screens it gives up its spare height but never its content, and it
          // grows with large system fonts. Without the min-height, overflow: hidden would
          // let flexbox squash it to nothing.
          height: { xs: 240, sm: 260 },
          minHeight: "min-content",
          mb: "auto",
          borderRadius: 3,
          overflow: "hidden",
          background: "linear-gradient(135deg, #e6f0ff 0%, #cddeff 100%)",
          border: "1px solid rgba(21,53,189,0.12)",
          p: 2.5,
          boxShadow: "0px 4px 20px rgba(21,53,189,0.08)",
          cursor: randomEntry ? "pointer" : "default",
          animation: "pageIn 0.22s ease-out backwards",
          animationDelay: "200ms",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
          {/* The card's labels are chrome: a long press shouldn't select them. */}
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: "0.7rem",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "#1535BD",
              opacity: 0.6,
              userSelect: "none",
            }}
          >
            Learn a new root
          </Typography>
          <IconButton
            size="small"
            aria-label="Show another root"
            onClick={(e) => { e.stopPropagation(); loadRandomEntry(); }}
            disabled={randomLoading}
            sx={{
              // 48px touch target; the negative margin keeps the header row's height
              p: "14px",
              m: "-9px",
              color: "#1535BD",
              opacity: 0.5,
              transition: "opacity 0.2s, transform 0.4s",
              "&:hover": { opacity: 0.9 },
              ...(randomLoading && { transform: "rotate(180deg)" }),
            }}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Box>

        {randomEntry ? (
          <Fade in timeout={300}>
            <Box>
              <Typography
                sx={{
                  fontFamily: "'Noto Naskh Arabic', 'Scheherazade', serif",
                  fontSize: "2rem",
                  lineHeight: 1.3,
                  color: "#1535BD",
                  fontWeight: 700,
                }}
              >
                {randomEntry.forms[0].text || randomEntry.root}
              </Typography>
              {/* Some entries have no transliteration; the form numeral is in the Forms row. */}
              {randomEntry.forms[0].transliteration?.trim() ? (
                <Typography
                  sx={{
                    fontSize: "0.78rem",
                    color: "#1535BD",
                    opacity: 0.6,
                    fontStyle: "italic",
                    mb: 1,
                  }}
                >
                  ({randomEntry.forms[0].transliteration}{randomEntry.forms[0].form ? ` ${randomEntry.forms[0].form}` : ""})
                </Typography>
              ) : null}
              <Typography
                sx={{
                  fontSize: "0.85rem",
                  color: "#333",
                  lineHeight: 1.5,
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  mb: 1.5,
                }}
                dangerouslySetInnerHTML={{ __html: randomEntry.forms[0].translation.text }}
              />
              {(() => {
                const formNumerals = [...new Set(
                  randomEntry.forms.map((f) => f.form).filter(Boolean)
                )];
                if (!formNumerals.length) return null;
                return (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mt: 0.5, userSelect: "none" }}>
                    <Typography sx={{ fontSize: "0.65rem", fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: "#1535BD", opacity: 0.45 }}>
                      Forms
                    </Typography>
                    {formNumerals.map((f) => (
                      <Box
                        key={f}
                        sx={{
                          fontSize: "0.7rem",
                          fontWeight: 700,
                          color: "#1535BD",
                          bgcolor: "rgba(21,53,189,0.08)",
                          borderRadius: "6px",
                          px: 0.9,
                          py: 0.2,
                          lineHeight: 1.6,
                        }}
                      >
                        {f}
                      </Box>
                    ))}
                  </Box>
                );
              })()}
            </Box>
          </Fade>
        ) : (
          <Box>
            <Box sx={{
              height: 36, width: "55%", borderRadius: 1.5, mb: 1,
              background: "linear-gradient(90deg, rgba(21,53,189,0.07) 25%, rgba(21,53,189,0.13) 50%, rgba(21,53,189,0.07) 75%)",
              backgroundSize: "200% 100%",
              animation: "shimmer 1.4s infinite",
              "@keyframes shimmer": { "0%": { backgroundPosition: "200% 0" }, "100%": { backgroundPosition: "-200% 0" } },
            }} />
            <Box sx={{
              height: 14, width: "35%", borderRadius: 1, mb: 1.5,
              background: "linear-gradient(90deg, rgba(21,53,189,0.07) 25%, rgba(21,53,189,0.13) 50%, rgba(21,53,189,0.07) 75%)",
              backgroundSize: "200% 100%",
              animation: "shimmer 1.4s infinite 0.1s",
            }} />
            <Box sx={{
              height: 12, width: "90%", borderRadius: 1, mb: 0.75,
              background: "linear-gradient(90deg, rgba(21,53,189,0.07) 25%, rgba(21,53,189,0.13) 50%, rgba(21,53,189,0.07) 75%)",
              backgroundSize: "200% 100%",
              animation: "shimmer 1.4s infinite 0.2s",
            }} />
            <Box sx={{
              height: 12, width: "70%", borderRadius: 1,
              background: "linear-gradient(90deg, rgba(21,53,189,0.07) 25%, rgba(21,53,189,0.13) 50%, rgba(21,53,189,0.07) 75%)",
              backgroundSize: "200% 100%",
              animation: "shimmer 1.4s infinite 0.3s",
            }} />
          </Box>
        )}
      </Box>

      {/* <Tooltip title="Bookmarks">
        <IconButton
          to="/bookmarks"
          component={Link}
          sx={{
            borderRadius: 2,
            p: 2,
            color: "#fff",
            background: theme.palette.pink,
            boxShadow: "0px 10px 10px rgba(221, 114, 133, 0.2)",
            marginBottom: 20,
          }}
        >
          <BookmarkIcon />
        </IconButton>
      </Tooltip> */}

      {/* <Box
        sx={{
          position: "absolute",
          bottom: "calc(env(safe-area-inset-bottom, 0px) + 16px)",
          display: "flex",
          gap: 1.5,
          alignItems: "center",
        }}
      >
        <button
          onClick={() => setFeedbackOpen(true)}
          style={{ background: "none", border: "none", cursor: "pointer", padding: 0, margin: 0, font: "inherit", fontSize: "0.75rem", lineHeight: 1.66, color: "inherit", display: "inline" }}
        >
          Feedback
        </button>
        <Typography variant="caption" color="text.secondary">·</Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          component={Link}
          to="/privacy-policy"
          sx={{ color: "inherit", textDecoration: "none" }}
        >
          Privacy Policy
        </Typography>
      </Box> */}

      </Box> {/* inner content box */}

      {/* Version / changelog footer — pinned below the scrolling content.
          Bottom padding clears the badge's glow so the container's overflow:hidden doesn't clip it. */}
      <Box sx={{ flexShrink: 0, display: compact ? "none" : "flex", justifyContent: "center", pt: 0.5, pb: { xs: 1.5, sm: 1.5 } }}>
        <VersionBadge />
      </Box>

      {showHomeWalkthrough && (
        <HomeWalkthrough
          onDone={handleHomeWalkthroughDone}
          onSkip={handleHomeWalkthroughSkip}
        />
      )}

      <FeedbackDialog open={feedbackOpen} onClose={() => setFeedbackOpen(false)} mode="general" />

      <Dialog
        open={guideOpen}
        onClose={() => setGuideOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: "20px", overflow: "hidden", mt: "env(safe-area-inset-top, 24px)" } }}
      >
        <Box sx={{ px: 3, pt: 2.5, pb: 2, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid rgba(21,53,189,0.08)" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <KeyboardIcon sx={{ color: "#1535BD", fontSize: 20 }} />
            <Typography sx={{ fontWeight: 800, fontSize: "1.05rem", color: "#1535BD", fontFamily: "Mulish, sans-serif", letterSpacing: "-0.01em" }}>
              Typing Guide
            </Typography>
          </Box>
          <IconButton size="small" aria-label="Close" onClick={() => setGuideOpen(false)} sx={{ color: "rgba(0,0,0,0.35)", mr: -0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <DialogContent sx={{ pt: 2, pb: 3, px: 3 }}>
          <Box sx={{ bgcolor: "rgba(21,53,189,0.05)", borderRadius: "12px", p: 1.5, mb: 2.5, border: "1px solid rgba(21,53,189,0.08)" }}>
            <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.6, fontSize: "0.8rem" }}>
              Type English letters to search Arabic roots. Capitalisation matters for some. For multi-letter combos, use spaces:{" "}
              <Box component="span" sx={{ fontFamily: "monospace", fontWeight: 700, bgcolor: "rgba(21,53,189,0.1)", color: "#1535BD", px: 0.6, borderRadius: "4px", fontSize: "0.78rem" }}>th l j</Box>
              {" "}→ ث ل ج
            </Typography>
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0.75 }}>
            {[
              ["a", "ا"],
              ["b", "ب"],
              ["t", "ت"],
              ["v / th", "ث"],
              ["j", "ج"],
              ["H / 7", "ح"],
              ["x / kh / 5", "خ"],
              ["d", "د"],
              ["dh", "ذ"],
              ["r", "ر"],
              ["z", "ز"],
              ["s", "س"],
              ["sh", "ش"],
              ["S / 9", "ص"],
              ["D", "ض"],
              ["T / 6", "ط"],
              ["Z", "ظ"],
              ["e / 3", "ع"],
              ["g / gh", "غ"],
              ["f", "ف"],
              ["q / 8", "ق"],
              ["k", "ك"],
              ["l", "ل"],
              ["m", "م"],
              ["n", "ن"],
              ["h", "ه"],
              ["w", "و"],
              ["y", "ي"],
              ["2", "ء"],
            ].map(([latin, arabic]) => (
              <Box
                key={latin}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 1.25,
                  py: 0.75,
                  borderRadius: "10px",
                  bgcolor: "rgba(21,53,189,0.03)",
                  border: "1px solid rgba(21,53,189,0.06)",
                  transition: "background-color 0.15s",
                  "&:hover": { bgcolor: "rgba(21,53,189,0.07)" },
                }}
              >
                <Typography sx={{
                  fontFamily: "monospace",
                  fontWeight: 700,
                  fontSize: "0.75rem",
                  color: "#1535BD",
                  bgcolor: "rgba(21,53,189,0.1)",
                  borderRadius: "6px",
                  px: 0.75,
                  py: 0.3,
                  minWidth: 44,
                  textAlign: "center",
                  lineHeight: 1.5,
                  flexShrink: 0,
                }}>
                  {latin}
                </Typography>
                <Typography sx={{ fontFamily: "'Noto Naskh Arabic', serif", fontSize: "1.3rem", color: "#222", lineHeight: 1 }}>
                  {arabic}
                </Typography>
              </Box>
            ))}
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Home;
